import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import './styles.css';
import { useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';

interface VideoComponentProps {
  posterUrl?: string;
  videoUrl: string;
  autoplay?: boolean;
  styles?: any;
  hideController?: boolean;
  useProcessedFrame?: boolean;
  backgroundUrl?: string;
  preview?: boolean;
  audioUrl?: string | null;
  audioVolume?: number;
  videoVolume?: number;
}

// eslint-disable-next-line react/display-name
const VideoComponent = forwardRef<HTMLVideoElement, VideoComponentProps>(
  (
    {
      posterUrl,
      videoUrl,
      autoplay = false,
      styles = {},
      hideController = false,
      useProcessedFrame = false,
      preview = true,
      audioUrl,
      audioVolume = 100,
      videoVolume = 100,
      backgroundUrl
    },
    ref
  ) => {
    const [processedPoster, setProcessedPoster] = useState<string | null>(
      posterUrl || null
    );
    const [isFrameReady, setIsFrameReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useImperativeHandle(ref, () => videoRef.current, [videoUrl]);

    const {
      ui: { uploadAreaVisible }
    } = useSelector((state: lomavisCreatorRootState) => state.lomavisCreator);

    useEffect(() => {
      if (!useProcessedFrame) return;
      if (backgroundUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = backgroundUrl;
        img.onload = () => {
          processImage(img);
        };
        img.onerror = () => {
          console.error('Failed to load poster image from posterUrl');
        };
      } else {
        console.error('posterUrl is required when useProcessedFrame is true');
      }
    }, [backgroundUrl, useProcessedFrame]);

    useEffect(() => {
      if (posterUrl) {
        const videoElement =
          videoRef && 'current' in videoRef ? videoRef.current : null;
        if (videoElement) {
          // Pause the video to apply the poster
          videoElement.pause();

          // Reset the current time and reload to apply the new poster
          videoElement.currentTime = 0;
          videoElement.load();
        }
      }
    }, [posterUrl]);

    const processImage = (img: HTMLImageElement) => {
      const originalCanvas = document.createElement('canvas');
      const scaledCanvas = document.createElement('canvas');
      const blurredCanvas = document.createElement('canvas');
      const finalCanvas = document.createElement('canvas');

      const scaleDownWidth = 32;
      const scaleDownHeight = 18;
      const scaleUpWidth = 1080;
      const scaleUpHeight = 1920;
      // Step 1: Draw the original frame (from the poster image)
      drawImageOnCanvas(originalCanvas, img, img.width, img.height);

      // Step 2: Scale down the image to 32x18
      drawImageOnCanvas(scaledCanvas, img, scaleDownWidth, scaleDownHeight);

      // Step 3: Apply a simple blur to the scaled-down image
      applyBlur(scaledCanvas, blurredCanvas);

      // Step 4: Scale up the blurred image to 1080x1920
      const blurredImg = new Image();
      blurredImg.src = blurredCanvas.toDataURL();
      blurredImg.onload = () => {
        drawImageOnCanvas(finalCanvas, blurredImg, scaleUpWidth, scaleUpHeight);

        // Step 5: Set the processed frame as the poster
        setProcessedPoster(finalCanvas.toDataURL());
        // Set the image as ready
        setIsFrameReady(true);
      };
    };

    function drawImageOnCanvas(
      canvas: HTMLCanvasElement,
      img: HTMLImageElement,
      width: number,
      height: number
    ) {
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
      }
    }

    function applyBlur(
      sourceCanvas: HTMLCanvasElement,
      targetCanvas: HTMLCanvasElement
    ) {
      const ctx = targetCanvas.getContext('2d');
      if (ctx) {
        targetCanvas.width = sourceCanvas.width;
        targetCanvas.height = sourceCanvas.height;

        ctx.filter = 'blur(10px)'; // Simple blur filter
        ctx.drawImage(sourceCanvas, 0, 0);
      }
    }

    useEffect(() => {
      if (videoRef.current) {
        if (!uploadAreaVisible) {
          videoRef.current.pause();
        }
      }
    }, [uploadAreaVisible]);

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.volume = Math.max(0, Math.min(1, videoVolume / 100));
      }
    }, [videoVolume, videoUrl]);

    useEffect(() => {
      // Depend on audioUrl as well so the volume is (re)applied when the
      // <audio> element first mounts after audioUrl becomes set. The audio
      // also gets scaled by the video element's current volume so that the
      // native video controls drive the music too (see the volumechange
      // listener below for live updates).
      const audioElement = audioRef.current;
      const videoElement = videoRef.current;
      if (!audioElement) return;
      const base = Math.max(0, Math.min(1, audioVolume / 100));
      const videoFactor = videoElement
        ? videoElement.muted
          ? 0
          : videoElement.volume
        : 1;
      audioElement.volume = base * videoFactor;
      audioElement.muted = videoElement ? videoElement.muted : false;
    }, [audioVolume, audioUrl, videoUrl]);

    useEffect(() => {
      const videoElement = videoRef.current;
      const audioElement = audioRef.current;

      if (!videoElement || !audioElement || !audioUrl) {
        return;
      }

      const syncAudioTime = () => {
        if (
          Number.isFinite(videoElement.currentTime) &&
          Math.abs(audioElement.currentTime - videoElement.currentTime) > 0.25
        ) {
          audioElement.currentTime = videoElement.currentTime;
        }
      };

      const handlePlay = () => {
        syncAudioTime();
        void audioElement.play().catch(() => undefined);
      };

      const handlePause = () => {
        audioElement.pause();
      };

      const handleEnded = () => {
        audioElement.pause();
        audioElement.currentTime = 0;
      };

      const handleSeeking = () => {
        syncAudioTime();
      };

      const handleRateChange = () => {
        audioElement.playbackRate = videoElement.playbackRate;
      };

      // When the user moves the native video volume slider (or hits mute on
      // the video controls), scale the music volume proportionally and mirror
      // the mute state so the two stay perceptually linked.
      const handleVolumeChange = () => {
        const base = Math.max(0, Math.min(1, audioVolume / 100));
        audioElement.volume = base * videoElement.volume;
        audioElement.muted = videoElement.muted;
      };

      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);
      videoElement.addEventListener('seeking', handleSeeking);
      videoElement.addEventListener('ratechange', handleRateChange);
      videoElement.addEventListener('volumechange', handleVolumeChange);

      return () => {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
        videoElement.removeEventListener('seeking', handleSeeking);
        videoElement.removeEventListener('ratechange', handleRateChange);
        videoElement.removeEventListener('volumechange', handleVolumeChange);
        audioElement.pause();
      };
    }, [audioUrl, videoUrl, audioVolume]);

    useEffect(() => {
      const videoElement = videoRef.current;
      if (videoElement !== null) {
        const calculateHeight = (): void => {
          const { videoWidth, videoHeight } = videoElement;
          if (videoWidth === 0 || videoHeight === 0) {
            return;
          }
          const newWidth =
            videoElement.offsetWidth > 0
              ? videoElement.offsetWidth
              : videoElement.clientWidth;
          const aspectRatio = videoWidth / videoHeight;
          console.log(
            'videoWidth:',
            videoWidth,
            'videoHeight:',
            videoHeight,
            'offsetWidth:',
            videoElement.offsetWidth,
            'clientWidth:',
            videoElement.clientWidth,
            'aspectRatio:',
            aspectRatio
          );
          videoElement.style.height = `${newWidth / aspectRatio}px`;
        };

        const handleMetadataLoaded = (): void => {
          calculateHeight();
        };

        videoElement.addEventListener('loadedmetadata', handleMetadataLoaded);

        const resizeObserver = new ResizeObserver(() => {
          calculateHeight();
        });
        resizeObserver.observe(videoElement);

        // Cleanup event listener and observer
        return () => {
          videoElement.removeEventListener(
            'loadedmetadata',
            handleMetadataLoaded
          );
          resizeObserver.disconnect();
        };
      }
    }, [videoUrl]);

    useEffect(() => {
      /*
       * We need to adjust the css property `object-fit` when the video enters/exits fullscreen to keep the
       * video shown correctly.
       * */
      if (videoRef.current) {
        const videoElement = videoRef.current;

        const handleFullscreenChange = () => {
          // @ts-expect-error webkitFullscreenElement is available in WebKit browsers.
          if (document.fullscreenElement || document.webkitFullscreenElement) {
            // Video is in fullscreen
            videoElement.style.objectFit = 'contain'; // Change to cover in fullscreen
          } else {
            // Video exited fullscreen
            videoElement.style.objectFit = 'cover'; // Revert to contain
          }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener(
          'webkitfullscreenchange',
          handleFullscreenChange
        );

        // Cleanup event listeners
        return () => {
          document.removeEventListener(
            'fullscreenchange',
            handleFullscreenChange
          );
          document.removeEventListener(
            'webkitfullscreenchange',
            handleFullscreenChange
          );
        };
      }
    }, []);

    return (
      <div
        className="w-100 h-100 video-container d-flex justify-content-center align-items-center"
        style={{
          // Conditionally add background styles if `useProcessedFrame` is true
          ...(useProcessedFrame && processedPoster && isFrameReady
            ? {
                backgroundImage: `url(${processedPoster})`,
                backgroundAttachment: 'fixed',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden',
                backgroundColor: 'white'
              }
            : {}),
          maxHeight: styles.maxHeight || '100%'
        }}
      >
        <video
          className="w-100"
          ref={videoRef}
          key={videoUrl}
          disablePictureInPicture
          controlsList="nodownload"
          preload="metadata"
          poster={posterUrl}
          autoPlay={autoplay}
          muted={autoplay} // Ensure autoplay videos are muted for Safari
          // playsInline // Helps prevent full-screen behavior on iOS Safari
          style={{
            ...styles,
            objectFit: preview ? 'cover' : 'contain',
            maxHeight: '100%'
          }}
          controls={!hideController}
          crossOrigin="anonymous"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        {audioUrl && (
          <audio ref={audioRef} src={audioUrl} preload="auto" />
        )}
      </div>
    );
  }
);

export default VideoComponent;
